import React from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "~/components/ui/card";
import { ProfileSectionHeader } from "~/components/ui/profile-section-header";
import { MarkdownContent } from "~/components/markdown-content";
import { OfferCard } from "~/components/offer-card";
import { CoachResultsCard } from "~/components/coach-results-card";
import { CertificateItem } from "~/components/ui/certificate-item";
import { Button } from "~/components/ui/button";
import { ResourceCard } from "~/components/ui/resource-card";
import { Feature, hasFeature } from "~/lib/has-feature";
import { ImageSparkle } from "~/components/ui/image-sparkle";
import { CDNImage } from "~/components/ui/cdn-image";
import { Badge } from "~/components/ui/badge";
import { VideoPlayer } from "~/components/ui/video-player";
import { CoachLeadMagnet } from "~/components/coach-lead-magnet";
import { coachQueryOptions } from "~/lib/query-options";
import { MembershipPlan } from "~/lib/schemas";
import type { CoachPublicData } from "~/shared/loaders";
import { createCoachMeta, createPersonStructuredData } from "~/lib/shared/meta";
import {
  shouldShowCoachCTAs,
  shouldShowMessagingCTAs,
  getCoachCTAText,
} from "~/lib/coach-cta-utils";
import { useOfferCheckout } from "~/hooks/use-offer-checkout";
import { Testimonials } from "~/components/ui/testimonials";

export const Route = createFileRoute("/$username/_profile/")({
  loader: async ({ context, params }) => {
    // Pre-load coach data into React Query using centralized query options
    const coach = await context.queryClient.ensureQueryData(
      coachQueryOptions.byUsername(params.username),
    );
    return { coach };
  },
  head: ({ loaderData }) => {
    const coach = loaderData?.coach;
    if (!coach || !coach.profile) {
      return {
        title: "Coach Profile - Dream Coach Match",
      };
    }

    const description =
      coach.profile.description ||
      `${coach.fullName} is a professional coach specializing in ${coach.profile.coachingTitle || "life coaching"}.`;

    // Determine best image for sharing
    const imageUrl =
      coach.profile.heroImageSrc ||
      coach.profilePictureUrl ||
      `https://dreamcoachmatch.com/${coach.username}/social-image.svg`;

    const currentUrl = `https://dreamcoachmatch.com/${coach.username}`;

    // Generate keywords from coach data
    const keywords = [
      "coaching",
      "life coach",
      coach.profile.coachingTitle || undefined,
      coach.profile.primaryCity || undefined,
      coach.profile.languages || undefined,
      ...coach.certifications.map((cert) => cert.title),
    ]
      .filter(Boolean)
      .filter((keyword): keyword is string => "string" === typeof keyword);

    // Use meta helpers for consistent SEO
    const metaTags = createCoachMeta(coach.fullName, {
      description,
      ogDescription: description,
      ogImage: imageUrl,
      ogUrl: currentUrl,
      twitterCard: imageUrl ? "summary_large_image" : "summary",
      canonical: currentUrl,
      author: coach.fullName,
      keywords,
    });

    // Enhanced structured data using helper
    const personStructuredData = createPersonStructuredData({
      name: coach.fullName,
      jobTitle: coach.profile.title || coach.profile.coachingTitle || undefined,
      image: coach.profile.mainImage || coach.profilePictureUrl || undefined,
      url: currentUrl,
      description,
      ...(coach.profile.primaryCity && {
        address: {
          addressLocality: coach.profile.primaryCity,
        },
      }),
    });

    // Add profile-specific OpenGraph tags
    const profileMetaTags = [
      // Profile-specific metadata
      ...(coach.firstName
        ? [
            {
              property: "og:profile:first_name" as const,
              content: coach.firstName,
            },
          ]
        : []),
      ...(coach.lastName
        ? [
            {
              property: "og:profile:last_name" as const,
              content: coach.lastName,
            },
          ]
        : []),
      {
        property: "og:profile:username" as const,
        content: coach.username,
      },
      // Geographic and language metadata
      ...(coach.profile?.primaryCity
        ? [
            {
              name: "geo.placename" as const,
              content: coach.profile.primaryCity,
            },
          ]
        : []),
      ...(coach.profile?.languages
        ? [
            {
              name: "language" as const,
              content: coach.profile.languages,
            },
          ]
        : []),
    ];

    return {
      title: `${coach.fullName} | Dream Coach Match`,
      meta: [...(metaTags.meta || []), ...profileMetaTags],
      links: metaTags.links,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(personStructuredData),
        },
      ],
    };
  },
  component: ProfilePage,
});

function ProfilePage() {
  const loaderData = Route.useLoaderData();
  const { coach } = loaderData;

  const { data: otherOffers = [] } = useQuery(
    coachQueryOptions.otherOffers(
      coach?.id || "",
      coach?.mainOffer?.id,
      coach?.membershipPlan,
    ),
  );

  if (!coach?.profile) {
    return <div>Coach not found.</div>;
  }

  return <MainProfile coach={coach} otherOffers={otherOffers} />;
}

export const MainProfile: React.FC<{
  coach: CoachPublicData;
  otherOffers: {
    id: string;
    headline: string | null;
    description: string | null;
    tags: { id: string; title: string }[];
  }[];
}> = ({ coach, otherOffers }) => {
  const { createCheckout } = useOfferCheckout();
  return (
    <div className="flex-basis-0 flex-grow-6 flex flex-col items-stretch gap-8">
      {(coach.profile.title || coach.profile.description) && (
        <div>
          <ProfileSectionHeader text="About Me" />
          <Card>
            <CardContent>
              <div className="flex gap-4 md:gap-8">
                {hasFeature(coach, Feature.FancyProfile) && (
                  <>
                    {coach.profile.mainVideo ? (
                      <div className="flex-basis-0 flex-grow-2 relative">
                        <VideoPlayer
                          src={coach.profile.mainVideo}
                          title={coach.fullName}
                          className="aspect-[2/3] w-full rounded-lg"
                        />
                        <ImageSparkle />
                      </div>
                    ) : (
                      coach.profile.mainImage && (
                        <div className="flex-basis-0 flex-grow-2 relative">
                          <CDNImage
                            src={coach.profile.mainImage}
                            alt={coach.fullName}
                            width={256}
                            height={384}
                            className="block aspect-[2/3] w-full rounded-lg object-cover"
                          />
                          <ImageSparkle />
                        </div>
                      )
                    )}
                  </>
                )}
                {(coach.profile.title || coach.profile.description) && (
                  <div className="flex-basis-0 flex-grow-4 flex w-full flex-col justify-start">
                    {coach.profile.title && (
                      <h2 className="text-text-primary font-heading text-3xl">
                        {coach.profile.title}
                      </h2>
                    )}
                    {coach.profile.description && (
                      <div className="text-text-secondary mt-4 border-l-2 border-l-orange-200 py-1 pl-2">
                        <MarkdownContent content={coach.profile.description} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {coach.profile.leadMagnetTitle && coach.profile.leadMagnetUrl && (
        <CoachLeadMagnet
          leadMagnetTitle={coach.profile.leadMagnetTitle}
          leadMagnetDescription={coach.profile.leadMagnetDescription}
          leadMagnetCtaText={coach.profile.leadMagnetCtaText}
          coachId={coach.id}
        />
      )}

      {coach.mainOffer?.headline && coach.mainOffer.description && (
        <div>
          <ProfileSectionHeader text="My Main Coaching Offer" />
          <OfferCard
            offer={coach.mainOffer}
            coach={coach}
            createCheckout={{
              mutate: ({ offerId }) => createCheckout.mutate(offerId),
              isPending: createCheckout.isPending,
              error: createCheckout.error?.message || null,
            }}
          />
        </div>
      )}

      {coach.membershipPlan === MembershipPlan.ProPlus &&
        0 < otherOffers.length && (
          <div>
            <ProfileSectionHeader text="Other Packages" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {otherOffers.map((offer) => (
                <Card key={offer.id}>
                  <CardContent className="flex h-full flex-col gap-4">
                    <div className="flex items-start gap-2">
                      <div>
                        {0 < offer.tags.length && (
                          <div className="flex items-center gap-2">
                            {offer.tags.map((t) => (
                              <Badge key={t.id} variant="outline-secondary">
                                {t.title}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <h3 className="font-heading mt-2 text-xl">
                          {offer.headline}
                        </h3>
                        <p className="text-secondary mt-2 line-clamp-3 text-sm">
                          {offer.description}
                        </p>
                      </div>
                    </div>
                    <div className="mt-auto">
                      <Link
                        to="/$username/offer/$offerId"
                        params={{ username: coach.username, offerId: offer.id }}
                      >
                        <Button variant="outline" className="w-full">
                          Details
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

      {(coach.profile.whyContent || coach.profile.whyTitle) && (
        <div>
          <ProfileSectionHeader text="Why Work With Me" />
          <Card>
            <CardContent className="space-y-4">
              {coach.profile.whyTitle && (
                <h2 className="font-heading text-2xl">
                  {coach.profile.whyTitle}
                </h2>
              )}

              {coach.profile.whyContent && (
                <div className="text-secondary mt-4 border-l-2 border-l-orange-200 py-1 pl-2">
                  <MarkdownContent content={coach.profile.whyContent} />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {0 < coach.results.length && (
        <div>
          <ProfileSectionHeader text="Results I help my clients achieve" />
          <CoachResultsCard results={coach.results} />
        </div>
      )}

      {0 < coach.certifications.length && (
        <div>
          <ProfileSectionHeader text="Certificates" />
          <Card>
            <CardContent>
              <div className="grid grid-cols-1 gap-4">
                {coach.certifications.map((cert) => (
                  <CertificateItem key={cert.id} {...cert} />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {0 < coach.testimonials?.length && (
        <div>
          <ProfileSectionHeader text="Testimonials" />
          <Testimonials testimonials={coach.testimonials} />
        </div>
      )}

      {hasFeature(coach, Feature.Resources) && 0 < coach.resources?.length && (
        <div>
          <ProfileSectionHeader text="Resources" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {coach.resources.slice(0, 2).map((resource) => (
              <ResourceCard
                className="h-full"
                key={resource.id}
                resource={resource}
                coachUsername={coach.username}
              />
            ))}
          </div>
        </div>
      )}

      {/* Contact/Booking CTA Button */}
      {shouldShowCoachCTAs(coach) && (
        <Link
          to={
            shouldShowMessagingCTAs(coach)
              ? "/$username/contact"
              : "/$username/book"
          }
          params={{ username: coach.username }}
          className="mx-auto mb-16"
        >
          <Button variant="fancy" size="lg">
            {getCoachCTAText(coach)}
          </Button>
        </Link>
      )}
    </div>
  );
};
